import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, Calendar, Clock, User, Phone, FileText, MessageSquare, ArrowLeft, BadgeCheck } from "lucide-react";
import { format, parseISO } from "date-fns";

export default function AppointmentDetail() {
  const location = useLocation();
  const navigate = useNavigate();
  const appointment = location.state?.appointment;

  // Set page title
  useEffect(() => {
    document.title = "Campos | Booking Appointment";
    return () => {
      document.title = "Campos | AI Agents"; // Reset on unmount
    };
  }, []);

  if (!appointment) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <p className="text-slate-600 mb-4">No appointment details found.</p>
            <Button onClick={() => navigate("/schedule-appointment")} variant="outline">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Schedule
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const dateStr = appointment.date;
  const timeStr = appointment.time?.replace(/:\d{2}$/, "") || appointment.time; // HH:MM:SS -> HH:MM
  let formattedDate = dateStr;
  try {
    const d = parseISO(dateStr);
    formattedDate = format(d, "EEEE, MMMM d, yyyy");
  } catch (_) {}

  const detailRows = [
    { icon: Calendar, label: "Date", value: formattedDate },
    { icon: Clock, label: "Time", value: timeStr },
    { icon: Clock, label: "Duration", value: appointment.duration ? `${appointment.duration} minutes` : "-" },
    { icon: FileText, label: "Service", value: appointment.service_name || appointment.service?.name || "-" },
    { icon: User, label: "Name", value: appointment.name || appointment.client?.name || "-" },
    { icon: Phone, label: "Phone", value: appointment.phone || appointment.client?.phone || "-" },
  ];
  if (appointment.status) {
    detailRows.push({ icon: BadgeCheck, label: "Status", value: String(appointment.status).charAt(0).toUpperCase() + String(appointment.status).slice(1) });
  }

  if (appointment.notes) {
    detailRows.push({ icon: MessageSquare, label: "Notes", value: appointment.notes });
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="max-w-lg w-full shadow-lg">
        <CardHeader>
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
          </div>
          <CardTitle className="text-xl text-center">Appointment Created</CardTitle>
          <p className="text-sm text-slate-600 text-center mt-1">
            Your appointment has been submitted successfully.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-4 space-y-3">
            {detailRows.map((row) => (
              <div key={row.label} className="flex items-start gap-3">
                <row.icon className="h-5 w-5 text-slate-500 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">{row.label}</p>
                  <p className="text-sm font-medium text-slate-800 break-words">{row.value}</p>
                </div>
              </div>
            ))}
          </div>

          {(appointment.id || appointment.appointment_id) && (
            <p className="text-xs text-slate-500 text-center">
              Reference: {appointment.id || appointment.appointment_id}
            </p>
          )}

          <Button
            variant="outline"
            className="w-full"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
